import { IXmlNode } from '@/app-modules/server-communications';
import { injectable, inject } from 'inversify';
import { WinnersData } from '@/components/game/_domain';
import { GameIdentity } from '@/components/game/_domain/models';
import { Bingo90Winners } from '@/components/game/implementations/uk/bingo-90/_domain/models';
import { Bingo80Winners } from '@/components/game/implementations/uk/bingo-80/_domain/models';
import { Bingo75Winners } from '@/components/game/implementations/uk/bingo-75/_domain/models';
import { SEBingo75Winners } from '@/components/game/implementations/se/bingo-75/_domain/models';
import { ITBingo75Winners } from '@/components/game/implementations/it/bingo-75/_domain/models';
import { Bingo75LinesWinners } from '@/components/game/implementations/uk/bingo-75-lines/_domain/models';
import { Bingo50Winners } from '@/components/game/implementations/uk/bingo-50/_domain/models';
import { BingoLiteWinners } from '@/components/game/implementations/uk/bingo-lite/_domain/models';
import { PulseWinners } from '@/components/game/implementations/uk/pulse/_domain/models/';
import { PictureBingoWinners } from '@/components/game/implementations/uk/picture-bingo/_domain/models';
import { ESPictureBingoWinners } from '@/components/game/implementations/es/picture-bingo/_domain/models';
import { PiratesWinners } from '@/components/game/implementations/uk/pirates/_domain/models';
import { RollerCoasterWinners } from '@/components/game/implementations/uk/roller-coaster/_domain/models';
import { Lucky7Winners } from '@/components/game/implementations/uk/lucky-7/_domain/models';
import { CincoWinners } from '@/components/game/implementations/uk/cinco/_domain/models';
import { DefaultWinners } from '@/components/game/implementations/_default/_domain/models';
import { BlocksWinners } from '@/components/game/implementations/uk/blocks/_domain/models';
import { BingoRouletteWinners } from '@/components/game/implementations/uk/bingo-roulette/_domain/models';
import { TombolaRouletteWinners } from '@/components/game/implementations/uk/tombola-roulette/_domain/models';
import { MorphWinners } from '@/components/game/implementations/uk/morph/_domain/models/';
import { SimboliWinners } from '@/components/game/implementations/it/simboli/_domain/models';
import { SmorfiaWinners } from '@/components/game/implementations/it/smorfia/_domain/models';
import { PaperWinners } from '@/components/game/implementations/uk/paper/_domain/models';
import { HexWinners } from '@/components/game/implementations/uk/hex/_domain/models/winners';
import { BubbleBingoWinners } from '@/components/game/implementations/uk/bubble-bingo/_domain/models/winners';
import { NevadaWinners } from '@/components/game/implementations/uk/nevada/_domain/models/winners';
import { JigsawWinners } from '@/components/game/implementations/uk/jigsaw/_domain/models/winners';
import { BloomWinners } from '@/components/game/implementations/uk/bloom/_domain/models/winners';
import { ReelBingoWinners } from '@/components/game/implementations/uk/reel-bingo/_domain/models/winners';
import { PaintByNumbersWinners } from '@/components/game/implementations/uk/paint-by-numbers/_domain/models/winners';
import { Bingo30Winners } from '@/components/game/implementations/uk/bingo-30/_domain/models';
import { UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { StarshipWinners } from '@/components/game/implementations/uk/starship/_domain/models/winners';
import { WordsearchWinners } from '@/components/game/implementations/uk/wordsearch/_domain/models/winners';
import { DondBingoWinners } from '@/components/game/implementations/uk/dondbingo/_domain/models';

interface IWinnersDataFactory {
    getInstance(gameIdentity: GameIdentity, node: IXmlNode): WinnersData;
}

@injectable()
class WinnersDataFactory implements IWinnersDataFactory {

    private userPreferences: UserPreferences;

    constructor(
        @inject(SERVICE_IDENTIFIERS.IPersistedStore) private readonly persistedStore: IPersistedStore,
    ) {
        this.userPreferences = this.persistedStore.Get(UserPreferences);
    }

    public getInstance(gameIdentity: GameIdentity, node: IXmlNode): WinnersData {
        try {
            const country = this.userPreferences.country.toLowerCase();
            let gameIdentityName = gameIdentity.nameForConfiguration;
            if (gameIdentity.nameForConfiguration === 'bingo75') {
                // TODO: SE B75 and NL B75 is a special child, we need a better way of handling these cases
                if ((this.userPreferences.country.toLowerCase() === 'se' || this.userPreferences.country.toLowerCase() === 'nl')) {
                    gameIdentityName = 'seBingo75';
                }
                // TODO: This whole system needs reworks, this will fix it for now
                if (this.userPreferences.country.toLowerCase() === 'it') {
                    gameIdentityName = 'itBingo75';
                }
            }

            if (gameIdentity.nameForConfiguration === 'Picture Bingo') {
                if (this.userPreferences.country.toLowerCase() === 'es') {
                    gameIdentityName = 'esPictureBingo';
                }
            }
            return new (this.classNames as any)[gameIdentityName](node);
        } catch {
            return new DefaultWinners(node);
        }
    }

    private classNames = {
        bingo90: Bingo90Winners,
        bingo80: Bingo80Winners,
        bingo75: Bingo75Winners,
        bingo75lines: Bingo75LinesWinners,
        bingo50: Bingo50Winners,
        bingolite: BingoLiteWinners,
        bingoroulette: BingoRouletteWinners,
        cinco: CincoWinners,
        pulse: PulseWinners,
        pirates: PiratesWinners,
        picturebingo: PictureBingoWinners,
        rollercoaster: RollerCoasterWinners,
        lucky7: Lucky7Winners,
        blocks: BlocksWinners,
        tombolaroulette: TombolaRouletteWinners,
        morph: MorphWinners,
        simboli: SimboliWinners,
        smorfia: SmorfiaWinners,
        paper: PaperWinners,
        hex: HexWinners,
        bubblebingo: BubbleBingoWinners,
        nevada: NevadaWinners,
        jigsaw: JigsawWinners,
        bloom: BloomWinners,
        reelbingo: ReelBingoWinners,
        paintbynumbers: PaintByNumbersWinners,
        seBingo75: SEBingo75Winners,
        bingo30: Bingo30Winners,
        starship: StarshipWinners,
        itBingo75: ITBingo75Winners,
        wordsearch: WordsearchWinners,
        esPictureBingo: ESPictureBingoWinners,
        dondbingo: DondBingoWinners
    };
}

export { WinnersDataFactory, IWinnersDataFactory };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/_domain/server/_factory/winners-data-factory.ts